import React, { useMemo, useReducer, useState } from "react";
import { Spin } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { throttle } from "throttle-debounce";
import { MatterAutoCompleteDropdown } from "./matter-auto-complete-dropdown";
import { MatterFilterData, MatterValue } from "../../data-types";
import { InfiniteScrollSelect, InfiniteScrollSelectProps } from "../infinite-scroll-select";

import styles from "./matter-auto-complete.module.css";
import _ from "lodash";

const { ["dropdown-no-options"]: dropdownNoOptionsClassName } = styles as any;

declare type MatterAutoCompleteProps = {
  defaultValue?: MatterValue | MatterValue[] | null;
  value?: MatterValue | MatterValue[] | null;
  disabled?: boolean;
  onChangeMatter?: (matter: MatterValue) => void;
  onChangeMultipleMatters?: (matters: MatterValue[]) => void;
  matterFilterData: MatterFilterData;
  bordered?: boolean;
  classname?: string;
  debounceTimeout?: number;
  mode?: "multiple" | "tags";
  searchValue?: string;
} & Pick<InfiniteScrollSelectProps<any>, "onClear">;

declare type MatterOption = {
  matter: MatterValue;
  value: string;
  label: JSX.Element;
  selectedString: string;
};

const FirstSearch = (
  <>
    <h2>Search for a Matter</h2>
    <h4>Start typing to see Matters that match your search term.</h4>
  </>
);

const NoOptions = (
  <>
    <h2>No matters found</h2>
    <h4>Try different search terms.</h4>
  </>
);

export const MatterAutoComplete: React.FC<MatterAutoCompleteProps> = ({
  defaultValue,
  value,
  disabled = false,
  onChangeMatter,
  onChangeMultipleMatters,
  onClear,
  matterFilterData,
  classname,
  bordered = false,
  debounceTimeout = 800,
  mode,
  searchValue,
}) => {
  const { search, fetchMore, total, matters, loading } = matterFilterData;

  const [currentSearch, setCurrentSearch] = useState("");

  const matterOptions = renderMatterOptions(matters, currentSearch);

  const [displayState, displayDispatch] = useReducer(displayReducer, {
    searchOpen: false,
    addNewOpen: false,
  });

  const fetchRef = React.useRef(0);

  const onSearch = useMemo(() => {
    const loadOptions = (value: string) => {
      if (value.length > 1) {
        fetchRef.current += 1;
        const fetchId = fetchRef.current;
        setCurrentSearch(value);
        search(value);
        if (fetchId !== fetchRef.current) {
          return;
        }
      }
    };

    return throttle(debounceTimeout, loadOptions);
  }, [debounceTimeout, search]);

  const doOnLoadMore =
    (matters?.length || 0) >= total
      ? undefined
      : () => {
          fetchMore();
        };

  const firstSearch = matters === undefined;

  const NoOptionsOrFirstSearch = (
    <div className={dropdownNoOptionsClassName}>{firstSearch ? FirstSearch : NoOptions}</div>
  );

  return (
    <div>
      <InfiniteScrollSelect
        mode={mode}
        className={classname}
        optionLabelProp="selectedString"
        bordered={bordered}
        allowClear
        autoClearSearchValue={true}
        loading={loading}
        filterOption={false}
        clearIcon={<CloseOutlined />}
        onClear={onClear}
        defaultValue={
          Array.isArray(defaultValue)
            ? defaultValue.map((v: any) => matterValue(v))
            : defaultValue
            ? matterValue(defaultValue)
            : undefined
        }
        value={
          Array.isArray(value)
            ? value.map((v: any) => matterValue(v))
            : value
            ? matterValue(value)
            : undefined
        }
        disabled={disabled}
        showSearch={true}
        notFoundContent={loading ? <Spin /> : NoOptionsOrFirstSearch}
        dropdownMatchSelectWidth
        options={matterOptions}
        onLoadMore={doOnLoadMore}
        // https://github.com/react-component/select/issues/446 option can be undefined
        onChange={(_, option) => {
          if (mode === "multiple") {
            return (
              option &&
              onChangeMultipleMatters &&
              onChangeMultipleMatters(
                Array.isArray(option) ? option.map((o) => o.matter) : option.matter
              )
            );
          } else {
            return (
              option &&
              onChangeMatter &&
              onChangeMatter(Array.isArray(option) ? option.map((o) => o.matter) : option.matter)
            );
          }
        }}
        onSearch={onSearch}
        searchValue={searchValue}
        open={displayState.searchOpen || displayState.addNewOpen}
        onDropdownVisibleChange={(visible) =>
          displayDispatch({ type: visible ? "open-search" : "close-search" })
        }
        placeholder="Enter any part of matter's name."
        dropdownRender={(menus) => {
          return <MatterAutoCompleteDropdown>{menus}</MatterAutoCompleteDropdown>;
        }}
      />
    </div>
  );
};

function displayReducer(
  state: { searchOpen: boolean; addNewOpen: boolean },
  action: {
    type: "open-search" | "close-search" | "open-add-new" | "close-add-new";
  }
) {
  switch (action.type) {
    case "open-search":
      return { searchOpen: true, addNewOpen: false };
    case "close-search":
      return { searchOpen: false, addNewOpen: false };
    case "open-add-new":
      return { searchOpen: false, addNewOpen: true };
    case "close-add-new":
      return { searchOpen: true, addNewOpen: false };
    default:
      return state;
  }
}

function matterValue({ title, client }: MatterValue) {
  if (title === undefined) {
    return "";
  }
  let titleVal = title;
  if (client && titleVal.length > 30) {
    titleVal = titleVal.substring(0, 30) + "...";
  }
  return client ? `${titleVal} - ${client.name}` : title;
}

function renderMatterOptions(matters: MatterValue[], searchTerm: string): MatterOption[] {
  const uniqueMatters = _.uniqBy(matters, (matter) => matter.id);
  const filteredMatters = uniqueMatters.filter((matter) => matter !== undefined);
  return filteredMatters.map((matter) => {
    const { id } = matter;

    const displayValue = matterValue(matter);

    return {
      key: id,
      matter,
      value: displayValue,
      selectedString: displayValue,
      label: getHighlightedMatch(searchTerm, displayValue),
    };
  });
}

const getHighlightedMatch = (search: string, text: string) => {
  if (!text.trim() || !search.trim()) {
    return <span>{text}</span>;
  }

  const regex = new RegExp(`(${_.escapeRegExp(search.trim())})`, "gi");
  const parts = text.split(regex);

  return (
    <span>
      {parts
        .filter((part) => part)
        .map((part, i) =>
          regex.test(part) ? (
            <span style={{ color: "red" }} key={i}>
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
    </span>
  );
};
